import { ChevronDown, Clock, ExternalLink, FileCode2, FolderGit2, Globe, HardDrive, Network as NetworkIcon, Settings, Sparkles, Upload, XCircle, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { Network } from '@/types/network'
import type { Validation } from '@/lib/api-client'
import { cn } from '@/lib/utils'

type DiscoveryStatus = {
  status: string
  result?: string
  networkYaml?: string
  invariantsYaml?: string
  lastDiscoveryAt?: string
  confidence?: number
  pattern?: string
} | null

type InspectionStatus = {
  hasConfig: boolean
  networkYaml?: string
  invariantsYaml?: string
  bundleFiles?: BundleFile[]
  branch?: string
  commitSha?: string
  lastInspectedAt?: string
} | null

type ValidationStatusInfo = {
  status: string
  icon: LucideIcon
  color: string
}

type BundleFile = {
  path: string
  content: string
  htmlUrl?: string
}

type ConfigState = {
  hasConfigInSource: boolean
  hasGeneratedSnapshots: boolean
  hasSnapshotContent: boolean
  hasAnyConfig: boolean
  networkYaml?: string
  invariantsYaml?: string
  bundleFiles: BundleFile[]
  lastUpdatedAt?: string
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'N/A'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'N/A'
  }
  return date.toLocaleString()
}

const formatDateOnly = (value?: string | null) => {
  if (!value) {
    return 'N/A'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'N/A'
  }
  return date.toLocaleDateString()
}

const titleCase = (value?: string | null) => {
  if (!value) {
    return 'Unknown'
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const getValidationLabel = (validation: Validation) => {
  const typeLabel = titleCase(validation.type)
  return `${typeLabel} validation`
}

const buildSnapshotBundleFiles = (
  networkYaml?: string,
  invariantsYaml?: string,
): BundleFile[] => {
  const files: BundleFile[] = []

  if (networkYaml) {
    files.push({
      path: '.etherpilot/network.yml',
      content: networkYaml,
    })
  }

  if (invariantsYaml) {
    files.push({
      path: '.etherpilot/invariants.yml',
      content: invariantsYaml,
    })
  }

  return files
}

const getConfigState = (
  inspectionStatus: InspectionStatus,
  discoveryStatus: DiscoveryStatus,
): ConfigState => {
  const generatedNetworkYaml = discoveryStatus?.status === 'completed'
    ? discoveryStatus?.networkYaml
    : undefined
  const generatedInvariantsYaml = discoveryStatus?.status === 'completed'
    ? discoveryStatus?.invariantsYaml
    : undefined
  const networkYaml = inspectionStatus?.networkYaml || generatedNetworkYaml
  const invariantsYaml = inspectionStatus?.invariantsYaml || generatedInvariantsYaml
  const hasConfigInSource = Boolean(
    inspectionStatus?.hasConfig
    || inspectionStatus?.networkYaml
    || inspectionStatus?.invariantsYaml,
  )
  const bundleFiles = inspectionStatus?.bundleFiles?.length
    ? inspectionStatus.bundleFiles
    : buildSnapshotBundleFiles(networkYaml, invariantsYaml)
  const hasGeneratedSnapshots = Boolean(generatedNetworkYaml || generatedInvariantsYaml)
  const hasSnapshotContent = bundleFiles.length > 0

  return {
    hasConfigInSource,
    hasGeneratedSnapshots,
    hasSnapshotContent,
    hasAnyConfig: hasConfigInSource || hasGeneratedSnapshots,
    networkYaml,
    invariantsYaml,
    bundleFiles,
    lastUpdatedAt: inspectionStatus?.lastInspectedAt || discoveryStatus?.lastDiscoveryAt,
  }
}

const getSourceSummary = (network: Network) => {
  if (network.sourceType === 'github') {
    const sourceConfig = network.sourceConfig as { fullName?: string; owner?: string; name?: string; private?: boolean }
    return {
      label: sourceConfig.fullName || `${sourceConfig.owner || 'unknown'}/${sourceConfig.name || 'repository'}`,
      detail: sourceConfig.private ? 'Private GitHub repository' : 'GitHub repository',
      icon: FolderGit2,
    }
  }

  if (network.sourceType === 'http') {
    const sourceConfig = network.sourceConfig as { url?: string }
    return {
      label: sourceConfig.url || 'HTTP source',
      detail: 'Remote configuration endpoint',
      icon: Globe,
    }
  }

  if (network.sourceType === 'local') {
    const sourceConfig = network.sourceConfig as { path?: string }
    return {
      label: sourceConfig.path || 'Local source',
      detail: 'Local filesystem path',
      icon: HardDrive,
    }
  }

  if (network.sourceType === 'direct') {
    const sourceConfig = network.sourceConfig as { uploadedBy?: string; configCount?: number }
    return {
      label: sourceConfig.uploadedBy || 'Direct upload source',
      detail: typeof sourceConfig.configCount === 'number'
        ? `${sourceConfig.configCount} uploaded config${sourceConfig.configCount === 1 ? '' : 's'}`
        : 'Directly uploaded source',
      icon: Upload,
    }
  }

  return {
    label: `${titleCase(network.sourceType)} source`,
    detail: 'Managed outside repository-backed workflows',
    icon: NetworkIcon,
  }
}

const getSourceRows = (network: Network, configState: ConfigState) => {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Default branch', value: network.defaultBranch || 'main' },
    { label: 'Created', value: formatDateOnly(network.createdAt) },
    {
      label: 'Config status',
      value: configState.hasConfigInSource
        ? 'Config detected in source'
        : configState.hasGeneratedSnapshots
          ? 'Generated config available'
          : 'Config not detected',
    },
  ]

  if (network.sourceType === 'github') {
    const sourceConfig = network.sourceConfig as { fullName?: string; cloneUrl?: string }
    if (sourceConfig.cloneUrl) {
      rows.push({ label: 'Clone URL', value: sourceConfig.cloneUrl })
    }
  } else if (network.sourceType === 'http') {
    const sourceConfig = network.sourceConfig as { url?: string; pollingInterval?: number }
    if (sourceConfig.pollingInterval) {
      rows.push({ label: 'Polling interval', value: `${sourceConfig.pollingInterval} seconds` })
    }
  } else if (network.sourceType === 'local') {
    // The primary local path is already shown in the source summary row above.
  } else if (network.sourceType === 'direct') {
    const sourceConfig = network.sourceConfig as { uploadedBy?: string; configCount?: number; uploadedAt?: string }
    rows.splice(1, 0, { label: 'Uploaded by', value: sourceConfig.uploadedBy || 'N/A' })
    if (typeof sourceConfig.configCount === 'number') {
      rows.push({ label: 'Config count', value: String(sourceConfig.configCount) })
    }
    if (sourceConfig.uploadedAt) {
      rows.push({ label: 'Uploaded', value: formatDateTime(sourceConfig.uploadedAt) })
    }
  }

  rows.push({ label: 'Network ID', value: network.id })
  return rows
}

const getBundleFileGroups = (bundleFiles: BundleFile[]) => {
  const etherpilotFiles = bundleFiles.filter((file) => file.path.startsWith('.etherpilot/'))
  const configFiles = bundleFiles.filter((file) => !file.path.startsWith('.etherpilot/'))

  return [
    etherpilotFiles.length > 0 ? {
      title: 'EtherPilot files',
      description: 'The inspected `.etherpilot` source files from the selected GitHub tree.',
      files: etherpilotFiles,
    } : null,
    configFiles.length > 0 ? {
      title: 'Config files',
      description: 'Referenced device configs fetched from the same inspected tree.',
      files: configFiles,
    } : null,
  ].filter((group): group is { title: string; description: string; files: BundleFile[] } => Boolean(group))
}

const getBundleFileHint = (filePath: string) => {
  if (filePath.endsWith('.yml') || filePath.endsWith('.yaml')) {
    return 'YAML'
  }
  return 'config'
}

const getBundleFileName = (filePath: string) => {
  const segments = filePath.split('/')
  return segments[segments.length - 1] || filePath
}

function DebugCodeBlock({
  title,
  languageHint,
  content,
}: {
  title: string
  languageHint: string
  content?: string | null
}) {
  if (!content) {
    return null
  }

  return (
    <Collapsible className="rounded-xl border border-dashed border-border/40 bg-[#f7faff]">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/30 bg-background">
            <FileCode2 className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{title}</p>
            <p className="text-xs text-muted-foreground">Open {languageHint} only when you need raw detail</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Code view</span>
          <ChevronDown className="h-4 w-4 transition-transform data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        <pre className="max-h-96 overflow-x-auto rounded-lg border border-border/30 bg-background p-4 text-xs leading-6">
          <code>{content}</code>
        </pre>
      </CollapsibleContent>
    </Collapsible>
  )
}

function BundleFileSnapshot({
  file,
}: {
  file: BundleFile
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="break-all text-sm font-semibold text-foreground">{file.path}</p>
          <p className="text-xs text-muted-foreground">
            {file.path.startsWith('.etherpilot/')
              ? 'EtherPilot file from the inspected tree.'
              : 'Referenced config file from the inspected tree.'}
          </p>
        </div>
        {file.htmlUrl ? (
          <a
            className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline"
            href={file.htmlUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open ${file.path} on GitHub`}
          >
            Open on GitHub
            <ExternalLink className="h-4 w-4" />
          </a>
        ) : null}
      </div>
      <DebugCodeBlock
        title={getBundleFileName(file.path)}
        languageHint={getBundleFileHint(file.path)}
        content={file.content}
      />
    </div>
  )
}

export function NetworkOverviewSection({
  network,
  inspectionStatus,
  discoveryStatus,
}: {
  network: Network
  inspectionStatus: InspectionStatus
  discoveryStatus: DiscoveryStatus
}) {
  const sourceSummary = getSourceSummary(network)
  const SourceIcon = sourceSummary.icon
  const configState = getConfigState(inspectionStatus, discoveryStatus)
  const sourceRows = getSourceRows(network, configState)
  const sourceFiles = [
    {
      name: sourceSummary.label,
      summary: sourceSummary.detail,
      status: titleCase(network.sourceType),
      time: network.updatedAt || network.createdAt,
      primary: false,
    },
  ]

  return (
    <section>
      <Card className="overflow-hidden rounded-xl border border-border/30 shadow-none">
        <CardContent className="p-0">
          <div className="flex items-center justify-between gap-4 border-b border-border/20 bg-[#eef4fb] px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background text-primary">
                <SourceIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-foreground">{network.sourceType === 'github' ? 'network-bot' : 'Source adapter'}</p>
                <p className="truncate text-xs text-muted-foreground">{titleCase(network.sourceType)} source</p>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Latest source update: {formatDateTime(configState.lastUpdatedAt || network.updatedAt)}
            </div>
          </div>
          <div className="divide-y divide-border/20">
            {sourceFiles.map((file) => (
              <div key={file.name} className="grid gap-3 px-4 py-3 transition-colors hover:bg-[#f7faff] md:grid-cols-[1.4rem_minmax(0,1fr)_minmax(0,1.1fr)_7rem] md:items-center">
                <span className={cn('mt-0.5 text-primary md:mt-0', !file.primary && 'text-muted-foreground')}>
                  <FileCode2 className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{file.name}</p>
                </div>
                <p className="text-sm text-muted-foreground">{file.summary}</p>
                <div className="text-right text-xs text-muted-foreground">
                  <div>{file.status}</div>
                  <div>{formatDateOnly(file.time)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="grid gap-4 border-t border-border/20 px-4 py-4 md:grid-cols-2 xl:grid-cols-3">
            {sourceRows.map((row) => (
              <div key={row.label}>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{row.label}</p>
                <p className="mt-1 break-all text-sm text-foreground">{row.value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </section>
  )
}

export function ValidationWorkflowSection({
  network,
  validations,
  activeValidation,
  isCancellingValidation,
  onCancelValidation,
  onViewValidation,
  getValidationStatus,
}: {
  network: Network
  validations: Validation[]
  activeValidation: Validation | null
  isCancellingValidation: boolean
  onCancelValidation: () => void
  onViewValidation: (validationId: string) => void
  getValidationStatus: (validation: Validation) => ValidationStatusInfo
}) {
  const isExecutableFromUi = network.sourceType !== 'direct' && network.sourceType !== 'local' && network.sourceType !== 'cli'
  const latestValidation = validations[0]
  const latestValidationStatus = latestValidation ? getValidationStatus(latestValidation) : null
  const latestValidationResultLabel = latestValidation?.result?.toUpperCase() || latestValidation?.status.toUpperCase()

  return (
    <section className="space-y-6">
      <Card className="rounded-xl border border-border/30 shadow-none">
        <CardHeader>
          <CardTitle>Validation History</CardTitle>
          <CardDescription>
            Review current and recent validations here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {activeValidation ? (
            <div className="rounded-xl border border-border/30 bg-background p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">Current Validation</p>
                  <p className="text-sm text-muted-foreground">
                    {activeValidation.status === 'queued'
                      ? 'Validation is queued'
                      : activeValidation.status === 'pending'
                        ? 'Validation is pending'
                        : 'Validation is currently running'}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Clock className="h-4 w-4 animate-spin text-warning" />
                    <span>
                      {activeValidation.status === 'queued'
                        ? 'Validation request is queued...'
                        : activeValidation.status === 'pending'
                          ? 'Validation is pending...'
                          : 'Validation in progress...'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Started {formatDateTime(activeValidation.createdAt)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => onViewValidation(activeValidation.id)}>
                    Open Validation
                  </Button>
                  <Button size="sm" variant="destructive" onClick={onCancelValidation} disabled={isCancellingValidation}>
                    <XCircle className="mr-2 h-4 w-4" />
                    {isCancellingValidation ? 'Cancelling...' : 'Cancel Validation'}
                  </Button>
                </div>
              </div>
            </div>
          ) : latestValidation ? (
            <div className="rounded-xl border border-border/30 bg-background p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">Latest Validation</p>
                  <div className="flex items-center gap-2">
                    {latestValidationStatus ? (() => {
                      const StatusIcon = latestValidationStatus.icon
                      return <StatusIcon className={`h-4 w-4 ${latestValidationStatus.color}`} />
                    })() : null}
                    <p className="text-sm font-medium text-foreground">{latestValidationResultLabel}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getValidationLabel(latestValidation)} from {formatDateTime(latestValidation.createdAt)} is ready for detail review.
                  </p>
                </div>
                <Button size="sm" onClick={() => onViewValidation(latestValidation.id)}>
                  Open Latest Validation
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border/30 bg-[#f7faff] p-5">
              <p className="text-sm font-semibold text-foreground">No validation evidence yet</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {isExecutableFromUi
                  ? 'Start the first validation from the page header to establish a baseline and populate this history.'
                  : 'Validation history will appear here after a CLI or API run creates evidence for this network.'}
              </p>
            </div>
          )}

          {validations.length > 0 ? (
            <div className="space-y-3">
              {validations.slice(0, 8).map((validation) => {
                const { icon: StatusIcon, color } = getValidationStatus(validation)
                return (
                  <button
                    key={validation.id}
                    type="button"
                    className="flex w-full flex-col gap-3 rounded-lg border border-border/30 border-l-[3px] px-4 py-4 text-left transition-colors hover:bg-[#f7faff] sm:flex-row sm:items-center sm:justify-between"
                    onClick={() => onViewValidation(validation.id)}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-background">
                        <StatusIcon className={`h-4 w-4 ${color}`} />
                      </span>
                      <div className="min-w-0 space-y-1">
                        <p className="break-words text-sm font-semibold text-foreground">{getValidationLabel(validation)}</p>
                        <p className="break-words text-sm text-muted-foreground">
                          {formatDateTime(validation.createdAt)}
                          {validation.status ? ` • ${titleCase(validation.status)}` : ''}
                          {validation.result ? ` • ${titleCase(validation.result)}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-start text-sm text-muted-foreground sm:self-center">
                      <span className="rounded-full border border-border/40 px-2 py-1 text-xs font-medium">{validation.id}</span>
                      <span className="inline-flex items-center gap-1">
                        Open
                        <ExternalLink className="h-4 w-4" />
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {latestValidation?.results ? (
        <Card className="rounded-xl border border-border/30 shadow-none">
          <CardHeader>
            <CardTitle>Latest Validation Output</CardTitle>
            <CardDescription>
              Keep the summary view clean while leaving the raw result payload available on demand.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DebugCodeBlock
              title="Latest validation results JSON"
              languageHint="JSON"
              content={JSON.stringify(latestValidation.results, null, 2)}
            />
          </CardContent>
        </Card>
      ) : null}
    </section>
  )
}

export function ConfigurationWorkflowSection({
  network,
  inspectionStatus,
  discoveryStatus,
  isDiscoveryRunning,
  isDiscoveryFailed,
  running,
  onGenerate,
  onCancel,
  onPushToGitHub,
}: {
  network: Network
  inspectionStatus: InspectionStatus
  discoveryStatus: DiscoveryStatus
  isDiscoveryRunning: boolean
  isDiscoveryFailed: boolean
  running: boolean
  onGenerate: () => void
  onCancel: () => void
  onPushToGitHub: () => void
}) {
  const configState = getConfigState(inspectionStatus, discoveryStatus)
  const hasVisibleConfigZone = configState.hasAnyConfig || Boolean(discoveryStatus) || network.sourceType === 'github'
  if (!hasVisibleConfigZone) {
    return null
  }

  const hasGeneratedArtifacts = discoveryStatus?.status === 'completed'
  const canGenerate = network.sourceType === 'github'
  const showGenerateAction = canGenerate && !configState.hasAnyConfig
  const bundleFileGroups = getBundleFileGroups(configState.bundleFiles)
  const hasSnapshotContent = configState.hasSnapshotContent

  return (
    <section className="space-y-6">
      <Card className="rounded-xl border border-border/30 shadow-none">
        <CardHeader>
          <CardTitle>Bundle Contents</CardTitle>
          <CardDescription>
            {network.sourceType === 'github'
              ? 'Review the inspected EtherPilot files and referenced config files from the selected GitHub tree.'
              : 'Review the inspected EtherPilot snapshots when they are available.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasSnapshotContent ? (
            bundleFileGroups.map((group) => (
              <div key={group.title} className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-foreground">{group.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{group.description}</p>
                </div>
                {group.files.map((file) => (
                  <BundleFileSnapshot key={file.path} file={file} />
                ))}
              </div>
            ))
          ) : (
            <div className="rounded-lg border border-dashed border-border/30 bg-[#f7faff] px-4 py-8 text-center text-sm text-muted-foreground">
              Source-backed configuration will appear here after inspection or discovery.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-border/30 shadow-none">
        <CardHeader>
          <CardTitle>Discovery Actions</CardTitle>
          <CardDescription>
            Keep generation and push actions separate from validation so the workflow boundaries stay clear.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border/30 bg-[#f7faff] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Discovery status</p>
            <p className="mt-2 text-base font-semibold text-foreground">{titleCase(discoveryStatus?.status || 'not started')}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {isDiscoveryRunning
                ? 'Discovery is actively analyzing this network.'
                : isDiscoveryFailed
                  ? 'The last discovery attempt did not complete successfully.'
                  : hasGeneratedArtifacts
                    ? 'Generated discovery output is available for review.'
                    : 'Generate configuration when you need EtherPilot to propose source files.'}
            </p>
          </div>

          {isDiscoveryRunning ? (
            <Button className="w-full" variant="destructive" onClick={onCancel}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Discovery
            </Button>
          ) : null}

          {(showGenerateAction || configState.hasConfigInSource || isDiscoveryFailed) ? (
            <Button
              className="w-full"
              variant={configState.hasConfigInSource || isDiscoveryFailed ? 'outline' : 'default'}
              onClick={onGenerate}
              disabled={running || isDiscoveryRunning}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {configState.hasConfigInSource ? 'Regenerate' : isDiscoveryFailed ? 'Retry Discovery' : 'Generate Configuration'}
            </Button>
          ) : null}

          {network.sourceType === 'github' && hasGeneratedArtifacts ? (
            <Button className="w-full" variant="secondary" onClick={onPushToGitHub}>
              <Upload className="mr-2 h-4 w-4" />
              Push to GitHub
            </Button>
          ) : null}

          {!canGenerate ? (
            <div className="rounded-lg border border-border/40 bg-background px-4 py-3 text-sm text-muted-foreground">
              Discovery generation is limited for this source type. Review the available source context instead.
            </div>
          ) : null}
        </CardContent>
      </Card>
    </section>
  )
}

export function SettingsActionsSection({
  network,
  onOpenSettings,
}: {
  network: Network
  onOpenSettings: () => void
}) {
  return (
    <section>
      <Card className="rounded-xl border border-border/30 shadow-none">
        <CardHeader>
          <CardTitle>Network Settings</CardTitle>
          <CardDescription>
            Keep administrative settings distinct from validation and discovery workflows.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Manage metadata and source settings for {network.name}</p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Review source configuration, branch defaults, and network-level details without mixing them into the operational workflow.
            </p>
          </div>
          <Button variant="outline" onClick={onOpenSettings}>
            <Settings className="mr-2 h-4 w-4" />
            Open Settings
          </Button>
        </CardContent>
      </Card>
    </section>
  )
}

export type { DiscoveryStatus, InspectionStatus, ValidationStatusInfo }
